// WebGL2 board renderer: instanced rounded rects + glyph quads over a dynamic glyph atlas.
// The scene is rebuilt from the engine whenever the camera or the board changes; a depth value per
// card keeps text of a lower card from showing through a card above it.
import type { Cam } from "./camera";
import { INK_COLORS } from "./ink";
import { CARD_STRIDE, FLAG_CONFLICT, FLAG_DIMMED, FLAG_MISMATCH, LINK_STRIDE, type FrameSpec, type LaneLoad } from "./engine-api";
import { dayIn, formatPoints, monthWeeks, shortDate, weekInMonth, weekKey, weekRange } from "./iso";
import { curve, dashes, handlePoint, polyline, resizePoint, type Box } from "./links";
import { BACKLOG, BLUE, CANVAS, CAPTION, CONFLICT, CYAN, LANE_ALT, LINK_COLORS, MONTH_SKY, MUTED, NAVY, SKY, STATUS, STICKY, TEXT, THIS_WEEK, WHITE, loadColor, ownerColor, personColor, type RGB } from "./palette";
import { frameJoints, frameSections, SECTION_JOINT, SECTION_TAB, THIS_WEEK_FLAG } from "./sections";
import type { Store } from "./store";

const RF = 10; // rect floats: x y w h r g b a radius depth
const GF = 13; // glyph floats: x y w h u0 v0 u1 v1 r g b a depth
const LF = 10; // line floats: x0 y0 x1 y1 r g b a width(screen px) depth
const LINK_DEPTH = 0.92; // links: above frames (0.95), below cards (0.9 … 0.1)
const HEADER = 18;
const INK_DEPTH = 0.05; // drawings: above the cards (0.9 … 0.1)

const RECT_VS = `#version 300 es
layout(location=0) in vec2 corner;
layout(location=1) in vec4 rect;
layout(location=2) in vec4 color;
layout(location=3) in vec2 rd; // radius, depth
uniform vec4 cam; uniform vec2 view;
out vec2 vLocal; out vec2 vSize; out vec4 vColor; out float vRadius;
void main() {
  vec2 p = rect.xy + corner * rect.zw;
  vec2 s = (p - cam.xy) * cam.z / view * 2.0 - 1.0;
  gl_Position = vec4(s.x, -s.y, rd.y, 1.0);
  vLocal = corner * rect.zw; vSize = rect.zw; vColor = color; vRadius = rd.x;
}`;
const RECT_FS = `#version 300 es
precision highp float;
in vec2 vLocal; in vec2 vSize; in vec4 vColor; in float vRadius;
uniform vec4 cam;
out vec4 o;
void main() {
  vec2 h = vSize * 0.5;
  float r = min(vRadius, min(h.x, h.y));
  vec2 q = abs(vLocal - h) - h + r;
  float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0) - r;
  float a = vRadius > 0.0 ? clamp(0.5 - d * cam.z, 0.0, 1.0) : 1.0;
  if (a <= 0.0) discard;
  o = vec4(vColor.rgb * vColor.a * a, vColor.a * a);
}`;
const GLYPH_VS = `#version 300 es
layout(location=0) in vec2 corner;
layout(location=1) in vec4 rect;
layout(location=2) in vec4 uv;
layout(location=3) in vec4 color;
layout(location=4) in float depth;
uniform vec4 cam; uniform vec2 view;
out vec2 vUv; out vec4 vColor;
void main() {
  vec2 p = rect.xy + corner * rect.zw;
  vec2 s = (p - cam.xy) * cam.z / view * 2.0 - 1.0;
  gl_Position = vec4(s.x, -s.y, depth, 1.0);
  vUv = mix(uv.xy, uv.zw, corner); vColor = color;
}`;
const GLYPH_FS = `#version 300 es
precision highp float;
in vec2 vUv; in vec4 vColor;
uniform sampler2D atlas;
out vec4 o;
void main() {
  float a = texture(atlas, vUv).a * vColor.a;
  if (a < 0.01) discard;
  o = vec4(vColor.rgb * a, a);
}`;

// Images (H1): one textured quad per image, premultiplied alpha; fully transparent pixels do not
// write depth (so what lies behind stays visible).
const IMAGE_VS = `#version 300 es
layout(location=0) in vec2 corner;
uniform vec4 cam; uniform vec2 view; uniform vec4 box; uniform float depth;
out vec2 vUv;
void main() {
  vec2 p = box.xy + corner * box.zw;
  vec2 s = (p - cam.xy) * cam.z / view * 2.0 - 1.0;
  gl_Position = vec4(s.x, -s.y, depth, 1.0);
  vUv = corner;
}`;
const IMAGE_FS = `#version 300 es
precision highp float;
in vec2 vUv;
uniform sampler2D img; uniform float alpha;
out vec4 o;
void main() {
  vec4 c = texture(img, vUv) * alpha;
  if (c.a < 0.01) discard;
  o = c;
}`;
/** Images lie above the frames (0.95 …) and below links (0.92) and cards. */
const IMAGE_DEPTH = 0.935;

// Line segments as screen-aligned quads with a constant screen width and a soft edge.
const LINE_VS = `#version 300 es
layout(location=0) in vec2 corner;
layout(location=1) in vec4 seg;
layout(location=2) in vec4 color;
layout(location=3) in vec2 wd; // width (screen px), depth
uniform vec4 cam; uniform vec2 view;
out float vAcross; out float vHalf; out vec4 vColor;
void main() {
  vec2 a = (seg.xy - cam.xy) * cam.z;
  vec2 b = (seg.zw - cam.xy) * cam.z;
  vec2 d = b - a;
  float len = max(length(d), 1e-4);
  vec2 t = d / len;
  vec2 n = vec2(-t.y, t.x);
  float hw = wd.x * 0.5 + 1.0;
  float across = (corner.y * 2.0 - 1.0) * hw;
  vec2 p = a + t * (corner.x * (len + hw) - hw * 0.5) + n * across;
  vec2 s = p / view * 2.0 - 1.0;
  gl_Position = vec4(s.x, -s.y, wd.y, 1.0);
  vAcross = across; vHalf = wd.x * 0.5; vColor = color;
}`;
const LINE_FS = `#version 300 es
precision highp float;
in float vAcross; in float vHalf; in vec4 vColor;
out vec4 o;
void main() {
  float a = clamp(vHalf + 0.5 - abs(vAcross), 0.0, 1.0) * vColor.a;
  if (a <= 0.0) discard;
  o = vec4(vColor.rgb * a, a);
}`;

/** Growable float buffer. */
class Floats {
  a = new Float32Array(4096);
  n = 0;
  push(...v: number[]) {
    if (this.n + v.length > this.a.length) {
      const b = new Float32Array(Math.max(this.a.length * 2, this.n + v.length));
      b.set(this.a.subarray(0, this.n));
      this.a = b;
    }
    this.a.set(v, this.n);
    this.n += v.length;
  }
  clear() { this.n = 0; }
}

interface Glyph { u0: number; v0: number; u1: number; v1: number; adv: number }

/** Glyph atlas drawn with Canvas 2D; grows as new characters appear. */
class Atlas {
  readonly S = 32;
  readonly PAD = 2;
  readonly CELL = Math.ceil(32 * 1.35);
  readonly BASE = Math.ceil(32 * 1.02);
  canvas = document.createElement("canvas");
  private ctx: CanvasRenderingContext2D;
  private glyphs = new Map<string, Glyph>();
  private x = 0;
  private y = 0;
  dirty = true;

  constructor(private font: string) {
    this.canvas.width = 2048;
    this.canvas.height = 1024;
    this.ctx = this.canvas.getContext("2d")!;
  }

  glyph(ch: string, bold: boolean): Glyph | undefined {
    const key = (bold ? "b" : "r") + ch;
    let g = this.glyphs.get(key);
    if (g) return g;
    const { ctx } = this;
    ctx.font = `${bold ? 600 : 400} ${this.S}px ${this.font}`;
    const adv = ctx.measureText(ch).width;
    const w = Math.ceil(adv) + 2 * this.PAD;
    if (this.x + w > this.canvas.width) { this.x = 0; this.y += this.CELL; }
    if (this.y + this.CELL > this.canvas.height) return undefined; // full: char is skipped
    ctx.fillStyle = "#fff";
    ctx.textBaseline = "alphabetic";
    ctx.fillText(ch, this.x + this.PAD, this.y + this.BASE);
    g = { u0: this.x / this.canvas.width, v0: this.y / this.canvas.height, u1: (this.x + w) / this.canvas.width, v1: (this.y + this.CELL) / this.canvas.height, adv };
    this.glyphs.set(key, g);
    this.x += w;
    this.dirty = true;
    return g;
  }

  width(s: string, size: number, bold: boolean) {
    let w = 0;
    for (const ch of s) w += this.glyph(ch, bold)?.adv ?? 0;
    return (w * size) / this.S;
  }
}

/** Word wrap that also breaks after hyphens and inside words wider than the card. */
function wrap(text: string, max: number, measure: (s: string) => number, maxLines: number): string[] {
  const pieces: { p: string; space: boolean }[] = [];
  text.split(/\s+/).filter(Boolean).forEach((w, wi) => w.split(/(?<=-)/).forEach((p, pi) => pieces.push({ p, space: wi > 0 && pi === 0 })));
  const lines: string[] = [];
  let cur = "";
  for (const { p, space } of pieces) {
    const cand = cur ? cur + (space ? " " : "") + p : p;
    if (measure(cand) <= max) { cur = cand; continue; }
    if (cur) lines.push(cur);
    cur = "";
    if (measure(p) <= max) { cur = p; continue; }
    let chunk = "";
    for (const ch of p) {
      if (chunk && measure(chunk + ch + "-") > max) { lines.push(chunk + "-"); chunk = ch; } else chunk += ch;
    }
    cur = chunk;
  }
  if (cur) lines.push(cur);
  if (lines.length > maxLines) {
    const cut = lines.slice(0, maxLines);
    cut[maxLines - 1] = cut[maxLines - 1].replace(/.$/, "…");
    return cut;
  }
  return lines;
}

/** Title sizes tried from large to small (card titles fill their card, C2). */
const TITLE_SIZES = [22, 20, 18, 16, 14, 13, 12, 11, 10];
export interface FittedTitle { size: number; line: number; lines: string[] }

/**
 * The largest size at which the whole title fits the card body without breaking a word; else the
 * largest at which it fits with long words split; else the smallest size with an ellipsis. `measure(text, size)` = text width.
 */
export function fitTitle(text: string, maxW: number, bodyH: number, measure: (s: string, size: number) => number): FittedTitle {
  const words = text.split(/\s+/).filter(Boolean);
  for (const size of TITLE_SIZES) {
    const line = Math.round(size * 1.25);
    const maxLines = Math.floor(bodyH / line);
    if (maxLines < 1) continue;
    if (words.some((w) => measure(w, size) > maxW)) continue; // a word would be split
    const lines = wrap(text, maxW, (t) => measure(t, size), Infinity);
    if (lines.length <= maxLines) return { size, line, lines };
  }
  // long words: the largest size at which the whole title fits with words split at the edge
  for (const size of TITLE_SIZES) {
    const line = Math.round(size * 1.25);
    const lines = wrap(text, maxW, (t) => measure(t, size), Infinity);
    if (lines.length <= Math.floor(bodyH / line)) return { size, line, lines };
  }
  const size = TITLE_SIZES[TITLE_SIZES.length - 1], line = Math.round(size * 1.25);
  return { size, line, lines: wrap(text, maxW, (t) => measure(t, size), Math.max(1, Math.floor(bodyH / line))) };
}

/** Big faint sign behind the title for finished, waiting and cancelled cards (C5). */
const WATERMARK: Record<number, string> = { 2: "✓", 3: "✕", 4: "◷" };

const n3 = (c: RGB): [number, number, number] => [c[0] / 255, c[1] / 255, c[2] / 255];
const clamp01 = (v: number) => Math.max(0, Math.min(1, v));
const mix = (a: RGB, b: RGB, t: number): RGB => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];

export interface RenderState {
  cam: Cam;
  selection: Set<string>;
  /** Cards being dragged and the current offset (board px). */
  drag: { ids: Set<string>; dx: number; dy: number } | null;
  editing: string | null;
  /** Selected link (drawn highlighted). */
  selectedLink: string | null;
  /** Card that shows the connector handle and the resize grip (single selection, not read-only). */
  handle: string | null;
  /** Card being resized and its new size (its top-right corner stays). */
  resize: { id: string; w: number; h: number } | null;
  /** Link being drawn from a card's handle to the pointer (board px). */
  linkDraft: { from: string; x: number; y: number; target: string | null } | null;
  /** Frame being dragged by its header: offset and its rect before the drag (board px). */
  frameDrag: { id: string; dx: number; dy: number; rect: [number, number, number, number] } | null;
  selectedFrame: string | null;
  /** Images (H1): the selected one, images being dragged, an image being resized. */
  selectedImage: string | null;
  imageDrag: { ids: Set<string>; dx: number; dy: number } | null;
  imageResize: { id: string; w: number; h: number } | null;
  /** Drawings: the stroke being drawn, selected strokes, their drag offset, strokes being erased. */
  ink?: { draft: { pts: number[]; color: number; kind: number } | null; selected: Set<string>; drag: { dx: number; dy: number } | null; hidden: Set<string> };
}

/** Extra margin around the view while something is dragged, so moving things stay visible. */
function dragPad(s: RenderState): number {
  const d = s.drag ?? s.frameDrag;
  return d ? Math.max(Math.abs(d.dx), Math.abs(d.dy)) + 200 : 0;
}

/** Offset of a card box during a drag: the dragged cards, or the cards of a dragged frame. */
function dragOffset(s: RenderState, id: string, x: number, y: number, w: number): [number, number] | null {
  if (s.drag?.ids.has(id)) return [s.drag.dx, s.drag.dy];
  const f = s.frameDrag;
  // a card belongs to the frame when its reference point (top-right corner) lies inside it
  if (f && x + w > f.rect[0] && x + w <= f.rect[0] + f.rect[2] && y >= f.rect[1] && y < f.rect[1] + f.rect[3]) return [f.dx, f.dy];
  return null;
}

export interface LinkGeom { id: string; pts: number[] }

export class BoardRenderer {
  private gl: WebGL2RenderingContext;
  private atlas: Atlas;
  private rects = new Floats();
  private glyphs = new Floats();
  private lines = new Floats();
  private inkRects = new Floats();
  private inkLines = new Floats();
  /** Drawings drawn in the last frame (tests). */
  lastStrokes = 0;
  private rectProg: WebGLProgram;
  private lineProg: WebGLProgram;
  private lineVao: WebGLVertexArrayObject;
  private lineBuf: WebGLBuffer;
  private uLine: { cam: WebGLUniformLocation; view: WebGLUniformLocation };
  private glyphProg: WebGLProgram;
  private rectVao: WebGLVertexArrayObject;
  private glyphVao: WebGLVertexArrayObject;
  private rectBuf: WebGLBuffer;
  private glyphBuf: WebGLBuffer;
  private tex: WebGLTexture;
  private imageProg: WebGLProgram;
  private imageVao: WebGLVertexArrayObject;
  private uImage: { cam: WebGLUniformLocation; view: WebGLUniformLocation; box: WebGLUniformLocation; depth: WebGLUniformLocation; alpha: WebGLUniformLocation };
  private textures = new Map<string, WebGLTexture>();
  private imageDraws: { file: string; x: number; y: number; w: number; h: number; depth: number; alpha: number }[] = [];
  /** Decoded pictures (images.ts ImageCache); null = not loaded yet. */
  imageSource: (file: string) => ImageBitmap | null = () => null;
  /** Images drawn in the last frame (tests). */
  lastImages = 0;
  private uRect: { cam: WebGLUniformLocation; view: WebGLUniformLocation };
  private uGlyph: { cam: WebGLUniformLocation; view: WebGLUniformLocation };
  /** Per card: title, owner, due date, marks (1 = description, 2 = link), assignees ("A,B"). */
  private textCache = new Map<string, [string, string, string, number, string, number | null]>();
  private textVersion = -1;
  private wrapCache = new Map<string, FittedTitle>();
  /** Cards drawn in the last frame, and how many of them were faded by the filter (tests). */
  lastVisible = 0;
  lastDimmed = 0;
  /** Today lines drawn in the last frame (tests). */
  lastToday = 0;
  /** This week's red frames drawn in the last frame (tests). */
  lastThisWeek = 0;
  /** Section tabs (sprint / masterplan) drawn in the last frame (tests). */
  lastSections: string[] = [];
  /** Load badges (X5) drawn in the last frame, as "frame lane week points/capacity" (tests). */
  lastLoads: string[] = [];
  /** Links drawn in the last frame with their polyline (board px), for hit testing. */
  linkGeom: LinkGeom[] = [];
  vw = 1;
  vh = 1;
  dpr = 1;

  constructor(private canvas: HTMLCanvasElement, font: string) {
    const gl = canvas.getContext("webgl2", { antialias: false, alpha: false, depth: true, premultipliedAlpha: true, powerPreference: "high-performance" });
    if (!gl) throw new Error("WebGL2 wird von diesem Browser nicht unterstützt.");
    this.gl = gl;
    this.atlas = new Atlas(font);
    const prog = (vs: string, fs: string) => {
      const p = gl.createProgram()!;
      for (const [t, src] of [[gl.VERTEX_SHADER, vs], [gl.FRAGMENT_SHADER, fs]] as const) {
        const s = gl.createShader(t)!;
        gl.shaderSource(s, src);
        gl.compileShader(s);
        if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s) || "shader");
        gl.attachShader(p, s);
      }
      gl.linkProgram(p);
      if (!gl.getProgramParameter(p, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(p) || "link");
      return p;
    };
    this.rectProg = prog(RECT_VS, RECT_FS);
    this.glyphProg = prog(GLYPH_VS, GLYPH_FS);
    this.lineProg = prog(LINE_VS, LINE_FS);
    this.imageProg = prog(IMAGE_VS, IMAGE_FS);
    this.uImage = {
      cam: gl.getUniformLocation(this.imageProg, "cam")!, view: gl.getUniformLocation(this.imageProg, "view")!,
      box: gl.getUniformLocation(this.imageProg, "box")!, depth: gl.getUniformLocation(this.imageProg, "depth")!,
      alpha: gl.getUniformLocation(this.imageProg, "alpha")!,
    };
    const loc = (p: WebGLProgram) => ({ cam: gl.getUniformLocation(p, "cam")!, view: gl.getUniformLocation(p, "view")! });
    this.uRect = loc(this.rectProg);
    this.uGlyph = loc(this.glyphProg);
    this.uLine = loc(this.lineProg);
    const quad = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, quad);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([0, 0, 1, 0, 0, 1, 1, 1]), gl.STATIC_DRAW);
    const vao = (layout: [number, number][], stride: number) => {
      const v = gl.createVertexArray()!;
      gl.bindVertexArray(v);
      gl.bindBuffer(gl.ARRAY_BUFFER, quad);
      gl.enableVertexAttribArray(0);
      gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
      const buf = gl.createBuffer()!;
      gl.bindBuffer(gl.ARRAY_BUFFER, buf);
      let off = 0;
      for (const [l, size] of layout) {
        gl.enableVertexAttribArray(l);
        gl.vertexAttribPointer(l, size, gl.FLOAT, false, stride * 4, off * 4);
        gl.vertexAttribDivisor(l, 1);
        off += size;
      }
      gl.bindVertexArray(null);
      return [v, buf] as const;
    };
    [this.rectVao, this.rectBuf] = vao([[1, 4], [2, 4], [3, 2]], RF);
    [this.glyphVao, this.glyphBuf] = vao([[1, 4], [2, 4], [3, 4], [4, 1]], GF);
    [this.lineVao, this.lineBuf] = vao([[1, 4], [2, 4], [3, 2]], LF);
    this.imageVao = gl.createVertexArray()!;
    gl.bindVertexArray(this.imageVao);
    gl.bindBuffer(gl.ARRAY_BUFFER, quad);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
    gl.bindVertexArray(null);
    this.tex = gl.createTexture()!;
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    gl.enable(gl.DEPTH_TEST);
    gl.depthFunc(gl.LEQUAL);
  }

  resize(cssW: number, cssH: number, dpr: number) {
    this.vw = cssW;
    this.vh = cssH;
    this.dpr = dpr;
    this.canvas.width = Math.max(1, Math.round(cssW * dpr));
    this.canvas.height = Math.max(1, Math.round(cssH * dpr));
  }

  // ---------- scene building ----------

  private rect(x: number, y: number, w: number, h: number, c: RGB, a: number, radius: number, depth: number) {
    const [r, g, b] = n3(c);
    this.rects.push(x, y, w, h, r, g, b, a, radius, depth);
  }

  private line(pts: number[], c: RGB, width: number, depth: number) {
    const [r, g, b] = n3(c);
    for (let i = 0; i + 3 < pts.length; i += 2) this.lines.push(pts[i], pts[i + 1], pts[i + 2], pts[i + 3], r, g, b, 1, width, depth);
  }

  /** Arrow head at the end of a polyline (two short strokes). */
  private arrow(pts: number[], c: RGB, width: number, depth: number, z: number) {
    const n = pts.length;
    const x = pts[n - 2], y = pts[n - 1];
    // direction from a point a little before the end (the last segment can be very short)
    const k = Math.max(0, n - 8);
    let dx = x - pts[k], dy = y - pts[k + 1];
    const l = Math.hypot(dx, dy) || 1;
    dx /= l; dy /= l;
    const len = 10 / z, ca = Math.cos(0.5), sa = Math.sin(0.5);
    for (const sgn of [1, -1]) {
      const ux = -(dx * ca - sgn * dy * sa), uy = -(sgn * dx * sa + dy * ca);
      this.line([x, y, x + ux * len, y + uy * len], c, width, depth);
    }
  }

  private links(store: Store, s: RenderState, view: { x0: number; y0: number; x1: number; y1: number }, z: number) {
    this.linkGeom = [];
    const pad = dragPad(s) + 50;
    const vl = store.visibleLinks(view.x0 - pad, view.y0 - pad, view.x1 - view.x0 + 2 * pad, view.y1 - view.y0 + 2 * pad);
    if (!vl.count && !s.linkDraft) return;
    const ids = vl.count ? vl.ids() : [];
    const b = vl.buf;
    const moved = (id: string, box: Box): Box => {
      const o = dragOffset(s, id, box.x, box.y, box.w);
      return o ? { ...box, x: box.x + o[0], y: box.y + o[1] } : box;
    };
    const segs = Math.max(8, Math.min(32, Math.round(24 * Math.sqrt(z))));
    for (let i = 0; i < vl.count; i++) {
      const o = i * LINK_STRIDE;
      const [id, from, to] = ids[i];
      const a = moved(from, { x: b[o], y: b[o + 1], w: b[o + 2], h: b[o + 3] });
      const c = moved(to, { x: b[o + 4], y: b[o + 5], w: b[o + 6], h: b[o + 7] });
      const color = b[o + 8], kind = b[o + 9], flags = b[o + 10] | 0;
      const pts = polyline(curve(a, c), segs);
      this.linkGeom.push({ id, pts });
      const dim = (flags & FLAG_DIMMED) !== 0;
      let rgb: RGB = flags & FLAG_CONFLICT ? CONFLICT : (LINK_COLORS[color] ?? LINK_COLORS[0]).rgb;
      if (dim) rgb = mix(rgb, CANVAS, 0.72);
      const sel = s.selectedLink === id;
      const width = sel ? 3 : 2;
      if (sel) this.line(pts, CYAN, 8, LINK_DEPTH + 1e-6);
      const dashed = this.dashedOf(store, id);
      for (const piece of dashed ? dashes(pts, 9 / z, 6 / z) : [pts]) this.line(piece, rgb, width, LINK_DEPTH);
      if (kind === 1) this.arrow(pts, rgb, width, LINK_DEPTH, z);
    }
    if (s.linkDraft) {
      const from = store.card(s.linkDraft.from);
      if (from) {
        const target = s.linkDraft.target ? store.card(s.linkDraft.target) : null;
        const end: Box = target ?? { x: s.linkDraft.x, y: s.linkDraft.y, w: 0, h: 0 };
        const pts = polyline(curve(from, end), segs);
        this.line(pts, BLUE, 2, 0.004);
        this.arrow(pts, BLUE, 2, 0.004, z);
      }
    }
  }

  /** Dash style lives in the link record; cached per store version to keep the hot path cheap. */
  private dashCache = new Map<string, boolean>();
  private dashVersion = -1;
  private dashedOf(store: Store, id: string): boolean {
    if (this.dashVersion !== store.version) { this.dashCache.clear(); this.dashVersion = store.version; }
    let d = this.dashCache.get(id);
    if (d === undefined) { d = store.link(id)?.dashed ?? false; this.dashCache.set(id, d); }
    return d;
  }

  /** Connector handle on the right edge of the selected card. */
  private handle(store: Store, s: RenderState, z: number) {
    if (!s.handle || s.drag || s.editing || z < 0.3) return;
    const c = store.card(s.handle);
    if (!c) return;
    const [hx, hy] = handlePoint(c, z);
    const r = 7 / z;
    this.rect(hx - r, hy - r, 2 * r, 2 * r, BLUE, 1, r, 0.003);
    const r2 = 5 / z;
    this.rect(hx - r2, hy - r2, 2 * r2, 2 * r2, WHITE, 1, r2, 0.002);
    const r3 = 2.5 / z;
    this.rect(hx - r3, hy - r3, 2 * r3, 2 * r3, BLUE, 1, r3, 0.001);
    // resize grip at the bottom-left corner (that corner moves, the top-right one stays)
    const g = s.resize?.id === c.id ? s.resize : null;
    const [gx, gy] = resizePoint(g ? { x: c.x + c.w - g.w, y: c.y, w: g.w, h: g.h } : c);
    const q = 5 / z, q2 = 3.5 / z;
    this.rect(gx - q, gy - q, 2 * q, 2 * q, BLUE, 1, 1.5 / z, 0.003);
    this.rect(gx - q2, gy - q2, 2 * q2, 2 * q2, WHITE, 1, 1 / z, 0.002);
  }

  private text(s: string, x: number, baseline: number, size: number, bold: boolean, c: RGB, a: number, depth: number, maxW = Infinity) {
    const at = this.atlas;
    const k = size / at.S;
    const [r, g, b] = n3(c);
    let pen = x;
    for (const ch of s) {
      const gl = at.glyph(ch, bold);
      if (!gl) continue;
      if (pen + gl.adv * k - x > maxW + 0.5) break;
      if (ch !== " ") this.glyphs.push(pen - at.PAD * k, baseline - at.BASE * k, (gl.adv + 2 * at.PAD) * k, at.CELL * k, gl.u0, gl.v0, gl.u1, gl.v1, r, g, b, a, depth);
      pen += gl.adv * k;
    }
  }

  private frames(frames: FrameSpec[], loads: Map<string, LaneLoad>, view: { x0: number; y0: number; x1: number; y1: number }, z: number, s: RenderState) {
    const labelA = clamp01((z - 0.12) / 0.1);
    const today = new Date();
    this.lastToday = 0;
    this.lastThisWeek = 0;
    this.lastSections = [];
    this.lastLoads = [];
    const line = NAVY;
    frames.forEach((f, fi) => {
      const w = f.cols.reduce((acc, c) => acc + c.width, 0);
      const h = f.rows.reduce((acc, r) => acc + r.height, 0);
      const fd = s.frameDrag?.id === f.id ? s.frameDrag : null;
      const fx = f.x + (fd?.dx ?? 0), fy = f.y + (fd?.dy ?? 0);
      const sections = frameSections(f);
      const tab = sections.length ? SECTION_TAB : 0;
      // the section tabs and the "Diese Woche" flag stick out above the frame
      if (fx > view.x1 || fy - tab - THIS_WEEK_FLAG > view.y1 || fx + w < view.x0 || fy + h < view.y0) return;
      // a dragged frame floats above the others (still below the cards)
      const depth = fd ? 0.93 : 0.95 - fi * 1e-4;
      if (s.selectedFrame === f.id || fd) {
        const t = 3 / z;
        this.rect(fx - t, fy - t, w + 2 * t, h + 2 * t, CYAN, 1, 0, depth + 1e-5);
      }
      let y = fy;
      f.rows.forEach((row, ri) => {
        let x = fx;
        const dateRow = row.kind === "header" || row.kind === "subHeader";
        f.cols.forEach((col, ci) => {
          const caption = col.kind.kind === "caption";
          const backlog = col.kind.kind === "backlog";
          const month = col.kind.kind === "month";
          const dated = month || col.kind.kind === "week";
          const next = f.cols[ci + 1]?.kind.kind;
          let fill: RGB = WHITE;
          if (row.kind === "header") fill = backlog ? BACKLOG : month ? BLUE : NAVY;
          else if (row.kind === "subHeader" || row.kind === "footer") fill = month ? MONTH_SKY : SKY;
          else if (caption) fill = CAPTION;
          else fill = ri % 2 ? LANE_ALT : WHITE;
          this.rect(x, y, col.width, row.height, fill, 1, 0, depth);
          // column dividers: the frame edge as before; white joints between the date cells, so every
          // week and month reads as its own tile; clear lines between weeks, stronger blue ones between months
          if (!next) this.rect(x + col.width - 1, y, 1, row.height, line, 0.12, 0, depth - 1e-6);
          else if (dateRow) this.rect(x + col.width - 1, y, 2, row.height, WHITE, 1, 0, depth - 1e-6);
          else if (month && next === "month") this.rect(x + col.width - 1, y, 2, row.height, BLUE, 0.35, 0, depth - 1e-6);
          else this.rect(x + col.width - 0.75, y, 1.5, row.height, line, dated ? 0.25 : 0.12, 0, depth - 1e-6);
          this.rect(x, y + row.height - 1, col.width, 1, line, dateRow ? 0.12 : 0.18, 0, depth - 1e-6);
          if (labelA > 0) {
            if (row.kind === "header") {
              const label = caption ? f.title : col.title;
              this.text(label, x + 12, y + 8 + 22, 22, true, WHITE, labelA, depth - 2e-6, col.width - 24);
            } else if (row.kind === "subHeader" && col.kind.kind === "week") {
              this.text(weekRange(col.kind.value.year, col.kind.value.week), x + 12, y + 8 + 14, 14, false, TEXT, labelA, depth - 2e-6, col.width - 24);
            } else if (row.kind === "subHeader" && col.kind.kind === "month") {
              this.text(monthWeeks(col.kind.value.year, col.kind.value.month), x + 12, y + 8 + 14, 14, false, TEXT, labelA, depth - 2e-6, col.width - 24);
            } else if (row.kind === "swimlane" && caption) {
              this.text(row.title, x + 12, y + 8 + 20, 20, true, TEXT, labelA, depth - 2e-6, col.width - 24);
            } else if (row.kind === "swimlane" && col.kind.kind === "week" && loads.size) {
              const load = loads.get(`${f.id}\t${row.id}\t${weekKey(col.kind.value.year, col.kind.value.week)}`);
              if (load?.capacity) this.loadBadge(f.id, row.id, load, x + col.width, y + row.height, col.width, labelA, depth);
            }
          }
          x += col.width;
        });
        y += row.height;
      });
      // sprint and masterplan as two parts: a joint in the board colour where weeks meet months
      // (over the dividers, under the labels' margins) and a tab above each part with its range
      for (const jx of frameJoints(f)) this.rect(fx + jx - SECTION_JOINT / 2, fy, SECTION_JOINT, h, CANVAS, 1, 0, depth - 2.5e-6);
      for (const sec of sections) {
        const color = sec.kind === "sprint" ? NAVY : BLUE;
        const tx = fx + sec.x0, tw = sec.x1 - sec.x0, ty = fy - tab;
        this.rect(tx, ty, tw, tab, color, 1, 8, depth);
        this.rect(tx, ty + tab / 2, tw, tab / 2, color, 1, 0, depth);
        if (labelA > 0) this.text(sec.label, tx + 12, ty + 23, 16, true, WHITE, labelA, depth - 2e-6, tw - 24);
        this.lastSections.push(sec.label);
      }
      // today line (F1): through the date rows and lanes of every week or month column holding today
      const top = f.rows.find((r) => r.kind === "header")?.height ?? 0;
      const bottom = f.rows.find((r) => r.kind === "footer")?.height ?? 0;
      let cx = fx;
      for (const col of f.cols) {
        const frac = dayIn(col.kind, today);
        if (frac !== null) {
          const lx = cx + frac * col.width, t = Math.max(2 / z, 1.5), r = Math.max(4 / z, 3);
          this.rect(lx - t / 2, fy + top, t, h - top - bottom, CYAN, 0.9, 0, depth - 3e-6);
          this.rect(lx - r, fy + top - r, 2 * r, 2 * r, CYAN, 1, r, depth - 3e-6);
          this.lastToday++;
        }
        cx += col.width;
      }
      this.thisWeek(f, fx, fy, h, tab, today, z, labelA, depth);
    });
  }

  /** This week in a red frame, from the tabs down to the last lane: the week column holding today,
   *  else (no such week column) the days of this week inside the month columns; a "Diese Woche"
   *  flag on top. Cards lie above it. */
  private thisWeek(f: FrameSpec, fx: number, fy: number, h: number, tab: number, today: Date, z: number, labelA: number, depth: number) {
    const spans: [number, number][] = [];
    let ox = 0;
    for (const col of f.cols) {
      if (col.kind.kind === "week" && dayIn(col.kind, today) !== null) spans.push([ox, ox + col.width]);
      ox += col.width;
    }
    if (!spans.length) {
      ox = 0;
      for (const col of f.cols) {
        const part = weekInMonth(col.kind, today);
        if (part) {
          const a = ox + part[0] * col.width, b = ox + part[1] * col.width;
          const last = spans[spans.length - 1];
          if (last && a - last[1] < 1) last[1] = b; // a week across two months: one frame
          else spans.push([a, b]);
        }
        ox += col.width;
      }
    }
    const t = Math.max(2.5 / z, 2), d = depth - 4e-6;
    for (const [a, b] of spans) {
      const x0 = fx + a, x1 = fx + b, top = fy - tab, bottom = fy + h;
      this.rect(x0, top, b - a, t, THIS_WEEK, 1, 0, d);
      this.rect(x0, bottom - t, b - a, t, THIS_WEEK, 1, 0, d);
      this.rect(x0, top, t, bottom - top, THIS_WEEK, 1, 0, d);
      this.rect(x1 - t, top, t, bottom - top, THIS_WEEK, 1, 0, d);
      if (labelA > 0) {
        const label = "Diese Woche", size = 13, ph = THIS_WEEK_FLAG;
        const pw = this.atlas.width(label, size, true) + 16;
        const px = (x0 + x1) / 2 - pw / 2, py = top - ph + t;
        this.rect(px, py, pw, ph, THIS_WEEK, labelA, 6, d);
        this.rect(px, py + ph / 2, pw, ph / 2, THIS_WEEK, labelA, 0, d);
        this.text(label, px + 8, py + 15.5, size, true, WHITE, labelA, d - 1e-6);
      }
      this.lastThisWeek++;
    }
  }

  /** Load vs capacity of a lane in one week (X5): a pill at the bottom right of the cell, green
   *  below 80 %, amber up to 100 %, red above. Cards placed over it cover it. */
  private loadBadge(frame: string, lane: string, load: LaneLoad, right: number, bottom: number, cellW: number, a: number, depth: number) {
    const { points, week } = load;
    const capacity = load.capacity!;
    const label = `${formatPoints(points)} / ${formatPoints(capacity)} SP`;
    const size = 13, ph = 20;
    const pw = this.atlas.width(label, size, true) + 14;
    if (pw + 12 > cellW) return;
    const x = right - pw - 8, y = bottom - ph - 8;
    this.rect(x, y, pw, ph, loadColor(points / capacity), points ? 0.95 : 0.55, ph / 2, depth - 2e-6);
    this.text(label, x + 7, y + 14.5, size, true, WHITE, a, depth - 3e-6);
    this.lastLoads.push(`${frame} ${lane} ${week} ${points}/${capacity}`);
  }

  /** Drawings (ink) above everything: a round dot per point (rect program, radius = half the
   *  width) and a segment between neighbours (line program); strokes of a dragged frame move along. */
  private ink(store: Store, s: RenderState, view: { x0: number; y0: number; x1: number; y1: number }, z: number) {
    this.inkRects.clear();
    this.inkLines.clear();
    this.lastStrokes = 0;
    const ink = s.ink, fd = s.frameDrag;
    store.strokes().forEach((st, i) => {
      if (ink?.hidden.has(st.id)) return;
      let dx = 0, dy = 0;
      if (ink?.drag && ink.selected.has(st.id)) { dx = ink.drag.dx; dy = ink.drag.dy; }
      else if (fd && st.frame === fd.id) { dx = fd.dx; dy = fd.dy; }
      if (st.x + dx > view.x1 || st.y + dy > view.y1 || st.x + st.w + dx < view.x0 || st.y + st.h + dy < view.y0) return;
      this.stroke(st.pts, dx, dy, st.color, st.kind, z, INK_DEPTH - i * 1e-6);
      if (ink?.selected.has(st.id)) {
        const t = 2 / z, g = 5 / z, x = st.x + dx - g, y = st.y + dy - g, w = st.w + 2 * g, h = st.h + 2 * g;
        const [r, gr, b] = n3(CYAN);
        for (const [rx, ry, rw, rh] of [[x, y, w, t], [x, y + h - t, w, t], [x, y, t, h], [x + w - t, y, t, h]]) this.inkRects.push(rx, ry, rw, rh, r, gr, b, 1, 0, 0.004);
      }
      this.lastStrokes++;
    });
    if (ink?.draft) this.stroke(ink.draft.pts, 0, 0, ink.draft.color, ink.draft.kind, z, 0.008);
  }

  private stroke(pts: number[], dx: number, dy: number, color: number, kind: number, z: number, depth: number) {
    const [r, g, b] = n3(INK_COLORS[color]?.rgb ?? NAVY);
    const a = kind === 1 ? 0.35 : 1; // the marker lets what lies below show through
    for (let i = 0; i + 2 < pts.length; i += 3) {
      const x = pts[i] + dx, y = pts[i + 1] + dy, w = pts[i + 2];
      this.inkRects.push(x - w / 2, y - w / 2, w, w, r, g, b, a, w / 2, depth);
      if (i + 5 < pts.length) this.inkLines.push(x, y, pts[i + 3] + dx, pts[i + 4] + dy, r, g, b, a, Math.max(((w + pts[i + 5]) / 2) * z, 0.75), depth);
    }
  }

  /** Images: a textured quad each (drawn after the rects), a placeholder while loading. */
  private images(store: Store, s: RenderState, view: { x0: number; y0: number; x1: number; y1: number }, z: number) {
    this.imageDraws = [];
    const list = store.images();
    list.forEach((img, i) => {
      let { x, y, w, h } = img;
      if (s.imageDrag?.ids.has(img.id)) { x += s.imageDrag.dx; y += s.imageDrag.dy; }
      if (s.imageResize?.id === img.id) { w = s.imageResize.w; h = s.imageResize.h; }
      if (x > view.x1 || y > view.y1 || x + w < view.x0 || y + h < view.y0) return;
      const depth = IMAGE_DEPTH - i * 1e-5;
      const bmp = this.imageSource(img.file);
      if (bmp) this.imageDraws.push({ file: img.file, x, y, w, h, depth, alpha: s.imageDrag?.ids.has(img.id) ? 0.85 : 1 });
      else {
        this.rect(x, y, w, h, SKY, 0.55, 0, depth); // still loading (or not readable)
      }
      if (s.selectedImage === img.id) {
        const t = 2 / z;
        this.rect(x - t, y - t, w + 2 * t, t, CYAN, 1, 0, depth - 2e-6);
        this.rect(x - t, y + h, w + 2 * t, t, CYAN, 1, 0, depth - 2e-6);
        this.rect(x - t, y, t, h, CYAN, 1, 0, depth - 2e-6);
        this.rect(x + w, y, t, h, CYAN, 1, 0, depth - 2e-6);
        // resize grip at the bottom-right corner (the aspect ratio stays)
        const q = 5 / z, q2 = 3.5 / z;
        this.rect(x + w - q, y + h - q, 2 * q, 2 * q, BLUE, 1, 1.5 / z, 0.003);
        this.rect(x + w - q2, y + h - q2, 2 * q2, 2 * q2, WHITE, 1, 1 / z, 0.002);
      }
    });
    this.lastImages = this.imageDraws.length;
  }

  private textureOf(file: string): WebGLTexture | null {
    let t = this.textures.get(file);
    if (t) return t;
    const bmp = this.imageSource(file);
    if (!bmp) return null;
    const gl = this.gl;
    t = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, t);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, bmp);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
    gl.generateMipmap(gl.TEXTURE_2D);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    this.textures.set(file, t);
    return t;
  }

  render(store: Store, s: RenderState) {
    const { gl } = this;
    const cam = s.cam;
    const view = { x0: cam.x, y0: cam.y, x1: cam.x + this.vw / cam.z, y1: cam.y + this.vh / cam.z };
    this.rects.clear();
    this.glyphs.clear();
    this.lines.clear();
    this.frames(store.frames(), store.loads(), view, cam.z, s);
    this.images(store, s, view, cam.z);
    this.links(store, s, view, cam.z);
    this.ink(store, s, view, cam.z);
    this.handle(store, s, cam.z);

    const textA = clamp01((cam.z - 0.3) / 0.12);
    // query a little wider than the view so cards dragged in from outside stay visible
    const pad = dragPad(s);
    const vis = store.visible(view.x0 - pad, view.y0 - pad, view.x1 - view.x0 + 2 * pad, view.y1 - view.y0 + 2 * pad);
    this.lastVisible = vis.count;
    const needIds = textA > 0 || s.selection.size > 0 || s.drag !== null || s.editing !== null;
    const ids = needIds ? vis.ids() : [];
    if (textA > 0) {
      if (this.textVersion !== store.version) { this.textCache.clear(); this.textVersion = store.version; }
      if (ids.some((id) => !this.textCache.has(id))) for (const [id, t, o, d, m, a, p] of vis.text()) this.textCache.set(id, [t, o, d, m ?? 0, a ?? "", p ?? null]);
    }
    const n = vis.count;
    const b = vis.buf;
    let dimmed = 0;
    for (let i = 0; i < n; i++) {
      const o = i * CARD_STRIDE;
      let x = b[o], y = b[o + 1];
      let w = b[o + 2], h = b[o + 3];
      const color = b[o + 4], status = b[o + 5], flags = b[o + 7] | 0;
      const mismatch = (flags & FLAG_MISMATCH) !== 0;
      // filtered out: faded towards the canvas colour, still opaque so the depth order stays intact
      const dim = (flags & FLAG_DIMMED) !== 0;
      if (dim) dimmed++;
      const fade = (c: RGB): RGB => (dim ? mix(c, CANVAS, 0.72) : c);
      const id = needIds ? ids[i] : "";
      // being resized: the new size, the top-right corner stays
      if (s.resize && s.resize.id === id) { x += w - s.resize.w; w = s.resize.w; h = s.resize.h; }
      const dragged = s.drag?.ids.has(id) ?? false;
      const off = s.drag || s.frameDrag ? dragOffset(s, id, x, y, w) : null;
      if (off) { x += off[0]; y += off[1]; }
      // back to front: later cards are nearer; dragged cards on top of everything
      const depth = dragged ? 0.05 - (i / (n + 1)) * 0.04 : 0.9 - ((i + 1) / (n + 1)) * 0.8;
      const sel = needIds && s.selection.has(id);
      const info = textA > 0 ? this.textCache.get(id) : undefined;
      const owner = info?.[1] ?? "";
      if (sel) this.rect(x - 3, y - 3, w + 6, h + 6, CYAN, 1, 7, depth + 3e-6);
      this.rect(x, y + 1, w, h, [0, 0, 0], dragged ? 0.25 : dim ? 0.04 : 0.14, 4, depth + 2e-6);
      const base = STICKY[color]?.rgb ?? STICKY[0].rgb;
      const body = fade(base);
      this.rect(x, y, w, h, body, 1, 4, depth);
      // without owner: a muted tint of the card colour (opaque, so the two header halves don't stack)
      const headerColor: RGB = fade(owner ? ownerColor(owner) : mix(base, MUTED, 0.35));
      this.rect(x, y, w, HEADER, headerColor, 1, 4, depth - 1e-6);
      this.rect(x, y + HEADER / 2, w, HEADER / 2, headerColor, 1, 0, depth - 1e-6);
      const st = STATUS[status] ?? STATUS[1];
      this.rect(x + w - 13, y + 5, 8, 8, fade(st.rgb), 1, 4, depth - 2e-6);
      if (textA > 0 && info && s.editing !== id) {
        const td = depth - 3e-6;
        // marks left of the status dot: description (three lines), link (arrow)
        const marks = info[3];
        let mx = x + w - 25;
        if (marks & 1) {
          for (let k = 0; k < 3; k++) this.rect(mx - 1, y + 5.5 + k * 2.6, 8 - (k === 2 ? 3 : 0), 1.3, fade(WHITE), 0.95, 0, depth - 2e-6);
          mx -= 12;
        }
        if (marks & 2) { this.text("↗", mx - 1, y + 13, 10, true, fade(WHITE), textA, td); mx -= 12; }
        if (owner) this.text(owner, x + 6, y + 13, 10, true, fade(WHITE), textA, td, Math.max(10, mx - x - 4));
        const mark = WATERMARK[status];
        if (mark) {
          const ms = Math.min(w, h - HEADER) * 0.62;
          const mw = this.atlas.width(mark, ms, true);
          this.text(mark, x + (w - mw) / 2, y + HEADER + (h - HEADER) / 2 + ms * 0.36, ms, true, fade(st.rgb), textA * 0.16, depth - 2.5e-6);
        }
        const key = `${info[0]}\u0000${w}\u0000${h}`;
        let fit = this.wrapCache.get(key);
        if (!fit) {
          fit = fitTitle(info[0], w - 12, h - HEADER - 24, (t, size) => this.atlas.width(t, size, false));
          if (this.wrapCache.size > 20000) this.wrapCache.clear();
          this.wrapCache.set(key, fit);
        }
        const f = fit;
        f.lines.forEach((l, li) => this.text(l, x + 6, y + HEADER + 4 + f.line * (li + 1) - Math.round(f.line * 0.22), f.size, false, fade(TEXT), textA, td));
        // assignees: a coloured dot each at the bottom right (three at most, then "+n")
        if (info[4]) {
          const people = info[4].split(",");
          const shown = people.slice(0, 3);
          shown.forEach((p, k) => this.rect(x + w - 14 - k * 11, y + h - 14, 9, 9, fade(personColor(p)), 1, 4.5, depth - 2e-6));
          if (people.length > 3) this.text(`+${people.length - 3}`, x + w - 22 - 3 * 11, y + h - 6, 9, true, fade(MUTED), textA, td);
        }
        let fx = x + 5; // footer, left to right: due date, story points
        if (info[2]) {
          const label = shortDate(info[2]);
          const pw = this.atlas.width(label, 10, true) + 10;
          if (mismatch) this.rect(fx, y + h - 17, pw, 13, fade(STATUS[3].rgb), 1, 6.5, depth - 2e-6);
          this.text(label, fx + 5, y + h - 7, 10, true, fade(mismatch ? WHITE : MUTED), textA, td);
          fx += pw + 3;
        }
        if (info[5] !== null) {
          const label = formatPoints(info[5]);
          const pw = Math.max(13, this.atlas.width(label, 10, true) + 8);
          this.rect(fx, y + h - 17, pw, 13, fade(NAVY), 0.82, 6.5, depth - 2e-6);
          this.text(label, fx + (pw - this.atlas.width(label, 10, true)) / 2, y + h - 7, 10, true, fade(WHITE), textA, td);
        }
      }
    }

    this.lastDimmed = dimmed;
    // drop target of a link being drawn: a ring just outside the card (never covers its text)
    const target = s.linkDraft?.target ? store.card(s.linkDraft.target) : null;
    if (target) {
      const t = 3 / cam.z, g = 2 / cam.z;
      const { x, y, w, h } = target;
      this.rect(x - g - t, y - g - t, w + 2 * (g + t), t, BLUE, 1, 0, 0.006);
      this.rect(x - g - t, y + h + g, w + 2 * (g + t), t, BLUE, 1, 0, 0.006);
      this.rect(x - g - t, y - g, t, h + 2 * g, BLUE, 1, 0, 0.006);
      this.rect(x + w + g, y - g, t, h + 2 * g, BLUE, 1, 0, 0.006);
    }

    // upload + draw
    if (this.atlas.dirty) {
      gl.bindTexture(gl.TEXTURE_2D, this.tex);
      gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, this.atlas.canvas);
      gl.generateMipmap(gl.TEXTURE_2D);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      this.atlas.dirty = false;
    }
    gl.viewport(0, 0, this.canvas.width, this.canvas.height);
    const [cr, cg, cb] = n3(CANVAS);
    gl.clearColor(cr, cg, cb, 1);
    gl.clearDepth(1);
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
    gl.useProgram(this.rectProg);
    gl.uniform4f(this.uRect.cam, cam.x, cam.y, cam.z, 0);
    gl.uniform2f(this.uRect.view, this.vw, this.vh);
    gl.bindVertexArray(this.rectVao);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.rectBuf);
    gl.bufferData(gl.ARRAY_BUFFER, this.rects.a.subarray(0, this.rects.n), gl.DYNAMIC_DRAW);
    gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, this.rects.n / RF);
    if (this.imageDraws.length) {
      gl.useProgram(this.imageProg);
      gl.uniform4f(this.uImage.cam, cam.x, cam.y, cam.z, 0);
      gl.uniform2f(this.uImage.view, this.vw, this.vh);
      gl.bindVertexArray(this.imageVao);
      for (const d of this.imageDraws) {
        const t = this.textureOf(d.file);
        if (!t) continue;
        gl.bindTexture(gl.TEXTURE_2D, t);
        gl.uniform4f(this.uImage.box, d.x, d.y, d.w, d.h);
        gl.uniform1f(this.uImage.depth, d.depth);
        gl.uniform1f(this.uImage.alpha, d.alpha);
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      }
    }
    if (this.lines.n) {
      gl.useProgram(this.lineProg);
      gl.uniform4f(this.uLine.cam, cam.x, cam.y, cam.z, 0);
      gl.uniform2f(this.uLine.view, this.vw, this.vh);
      gl.bindVertexArray(this.lineVao);
      gl.bindBuffer(gl.ARRAY_BUFFER, this.lineBuf);
      gl.bufferData(gl.ARRAY_BUFFER, this.lines.a.subarray(0, this.lines.n), gl.DYNAMIC_DRAW);
      gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, this.lines.n / LF);
    }
    if (this.glyphs.n) {
      gl.useProgram(this.glyphProg);
      gl.uniform4f(this.uGlyph.cam, cam.x, cam.y, cam.z, 0);
      gl.uniform2f(this.uGlyph.view, this.vw, this.vh);
      gl.bindVertexArray(this.glyphVao);
      gl.bindBuffer(gl.ARRAY_BUFFER, this.glyphBuf);
      gl.bufferData(gl.ARRAY_BUFFER, this.glyphs.a.subarray(0, this.glyphs.n), gl.DYNAMIC_DRAW);
      gl.bindTexture(gl.TEXTURE_2D, this.tex);
      gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, this.glyphs.n / GF);
    }
    if (this.inkRects.n) {
      // drawings last, on top; inside one stroke equal depths are drawn once (LESS), so a
      // translucent marker keeps one tone where its dots and segments overlap
      gl.depthFunc(gl.LESS);
      gl.useProgram(this.rectProg);
      gl.uniform4f(this.uRect.cam, cam.x, cam.y, cam.z, 0);
      gl.uniform2f(this.uRect.view, this.vw, this.vh);
      gl.bindVertexArray(this.rectVao);
      gl.bindBuffer(gl.ARRAY_BUFFER, this.rectBuf);
      gl.bufferData(gl.ARRAY_BUFFER, this.inkRects.a.subarray(0, this.inkRects.n), gl.DYNAMIC_DRAW);
      gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, this.inkRects.n / RF);
      if (this.inkLines.n) {
        gl.useProgram(this.lineProg);
        gl.uniform4f(this.uLine.cam, cam.x, cam.y, cam.z, 0);
        gl.uniform2f(this.uLine.view, this.vw, this.vh);
        gl.bindVertexArray(this.lineVao);
        gl.bindBuffer(gl.ARRAY_BUFFER, this.lineBuf);
        gl.bufferData(gl.ARRAY_BUFFER, this.inkLines.a.subarray(0, this.inkLines.n), gl.DYNAMIC_DRAW);
        gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, this.inkLines.n / LF);
      }
      gl.depthFunc(gl.LEQUAL);
    }
    gl.bindVertexArray(null);
  }
}
